import type { ScaleType, TribeId } from '@/types'
import colorsConfig from '@/config/colors.json'

/**
 * Get color for a response value based on scale type.
 */
export function getResponseColor(scaleType: ScaleType, responseLabel: string): string {
  const scales = colorsConfig.scales as Record<string, Record<string, string>>
  const scaleColors = scales[scaleType]

  if (scaleColors && scaleColors[responseLabel]) {
    return scaleColors[responseLabel]
  }

  // Try to find partial match
  for (const [key, color] of Object.entries(scaleColors || {})) {
    if (responseLabel.toLowerCase().includes(key.toLowerCase()) ||
        key.toLowerCase().includes(responseLabel.toLowerCase())) {
      return color
    }
  }

  // Default fallback color
  return '#bab0ac'
}

/**
 * Get color for a tribe.
 */
export function getTribeColor(tribeId: TribeId): string {
  const tribes = colorsConfig.tribes as Record<string, string>
  return tribes[tribeId] || '#4e79a7'
}

/**
 * Get all colors for a scale type.
 */
export function getScaleColors(scaleType: ScaleType): Record<string, string> {
  const scales = colorsConfig.scales as Record<string, Record<string, string>>
  return scales[scaleType] || {}
}

/**
 * Get divergent scale colors (for agree/disagree type charts).
 */
export function getDivergentColors(): {
  positive: string[]
  neutral: string
  negative: string[]
} {
  return colorsConfig.divergent
}

/**
 * Get chart styling colors.
 */
export function getChartColors(): typeof colorsConfig.chart {
  return colorsConfig.chart
}

// Tableau 10 palette for consistent cycling
export const TABLEAU_10 = [
  '#4e79a7', // blue
  '#f28e2b', // orange
  '#e15759', // red
  '#76b7b2', // teal
  '#59a14f', // green
  '#edc949', // yellow
  '#b07aa1', // purple
  '#ff9da7', // pink
  '#9c755f', // brown
  '#bab0ac', // gray
]

// Province colors - matching scatterplot exactly
export const PROVINCE_COLORS: Record<string, string> = {
  'British Columbia': '#4e79a7',  // blue
  'Alberta': '#f28e2b',           // orange
  'Saskatchewan': '#e15759',      // red
  'Manitoba': '#76b7b2',          // teal
  'Ontario': '#59a14f',           // green
  'Quebec': '#edc949',            // yellow
  'New Brunswick': '#b07aa1',     // purple
  'Nova Scotia': '#ff9da7',       // pink
  'Prince Edward Island': '#9c755f', // brown
  'Newfoundland & Labrador': '#bab0ac', // gray
  'North Provinces': '#86bcb6',   // light teal
  'Northern Territories': '#86bcb6', // light teal
  'Yukon': '#86bcb6',
  'NWT': '#7eb0a8',
  'Nunavut': '#6da49b',
}

// Ethnicity colors - distinct colors for each group
// Uses full labels from topline report with short label aliases
export const ETHNICITY_COLORS: Record<string, string> = {
  // Full labels from topline report D5
  'White': '#4e79a7',
  'Canadian identity': '#f28e2b',
  'Caucasian': '#59a14f',
  'British isles': '#76b7b2',
  'Western European': '#edc949',
  'East Asian descent': '#b07aa1',
  'Black / African / Caribbean descent': '#ff9da7',
  'Indigenous / First Nations / Aboriginal / Métis': '#8cd17d',
  'South Asian': '#e15759',
  'Francophone / Quebecois / Acadian': '#9c755f',
  'Eastern European / Slavic / Russian': '#86bcb6',
  'Middle Eastern / Arab': '#ffbe7d',
  'South East Asian': '#a0cbe8',
  'Mixed': '#d4a6c8',
  'Hispanic / Latino': '#fabfd2',
  'Jewish': '#d7b5a6',
  'Other': '#bab0ac',
  // Short label aliases
  'Canadian': '#f28e2b',
  'British Isles': '#76b7b2',
  'East Asian': '#b07aa1',
  'Black African': '#ff9da7',
  'Indigenous': '#8cd17d',
  'Quebecois': '#9c755f',
  'Eastern Euro': '#86bcb6',
  'Middle Eastern': '#ffbe7d',
  'Hispanic': '#fabfd2',
}

// Union membership colors - blue for yes/former, red for no
export const UNION_COLORS: Record<string, string> = {
  'Yes – a public sector union': '#005383',    // dark blue
  'Yes – a private sector union': '#0087bb',   // medium blue
  'Public sector union': '#005383',            // alias
  'Private sector union': '#0087bb',           // alias
  'I am a former union member': '#4aa8ce',     // light blue
  'Former union member': '#4aa8ce',            // alias
  'No': '#aa0503',                             // red
  'Unsure': '#bab0ac',                         // gray (will be excluded)
  'Other (specify)': '#bab0ac',                // gray (will be excluded)
}

// Genderation colors - blue gradient for male, pink gradient for female
// Lighter = younger, darker = older
export const GENDERATION_COLORS: Record<string, string> = {
  'Male_18-34': '#9fc5e8',    // light blue
  'Male_35-49': '#6fa8dc',    // medium light blue
  'Male_50-64': '#3d85c6',    // medium blue
  'Male_65+': '#1155cc',      // dark blue
  'Female_18-34': '#f4c2c2',  // light pink
  'Female_35-49': '#e891a8',  // medium light pink
  'Female_50-64': '#d45b8c',  // medium pink
  'Female_65+': '#c9285c',    // dark pink/magenta
}

// Financial situation colors - blue gradient (darker = wealthier)
export const FINANCIAL_COLORS: Record<string, string> = {
  'Falling behind on monthly expenses, in debt': '#c5d9ed',  // lightest blue
  'Making ends meet, but with no savings': '#9fc5e8',        // light blue
  'Getting by, with some savings': '#6fa8dc',                // medium blue
  'Financially secure': '#3d85c6',                           // darker blue
  'Well-off': '#1155cc',                                     // darkest blue
}

// Political party colors (official brand colors) - matching bar charts
export const PARTY_COLORS: Record<string, string> = {
  'Liberal Party of Canada': '#d71920',      // Liberal red
  'Conservative Party of Canada': '#1a4782', // Conservative blue
  'Bloc Québécois': '#33b2cc',               // Bloc light blue
  'New Democratic Party (NDP)': '#f37021',   // NDP orange
  'Green Party of Canada': '#3d9b35',        // Green
  "People's Party of Canada": '#5c3d7a',     // PPC purple
  'Other': '#9ca3af',                        // gray
  "I didn't vote": '#d1d5db',                // light gray
  "Not sure/ I don't recall": '#e5e7eb',     // lighter gray
  'Not eligible': '#f3f4f6',                 // very light gray
}

// Industry/Economic Sector colors - environmental footprint gradient
// Darker = higher carbon footprint, Green = renewable/nature-based
export const INDUSTRY_COLORS: Record<string, string> = {
  'Oil and gas': '#1a1a1a',                           // Black - highest footprint
  'Coal': '#4a4a4a',                                  // Dark gray
  'Construction or manufacturing': '#5d4037',         // Brown
  'Transportation, logistics, or storage': '#546e7a', // Blue-gray
  'Real estate or property management': '#6a1b9a',    // Purple
  'Finance and insurance': '#1565c0',                 // Blue
  'Professional services': '#00838f',                 // Teal
  'Retail services': '#c62828',                       // Red
  'Tourism, hospitality, and recreation': '#f9a825',  // Yellow
  'Arts, culture, and entertainment': '#ad1457',      // Pink/Magenta
  'Healthcare': '#d81b60',                            // Rose
  'Education and training': '#00acc1',                // Cyan
  'Food and agriculture': '#ef6c00',                  // Orange
  'Fishing': '#0277bd',                               // Light blue
  'Forestry': '#2e7d32',                              // Dark green
  'Renewable energy': '#7cb342',                      // Light green
}

// Media Sources colors - different category for each type
export const MEDIA_COLORS: Record<string, string> = {
  'TV News': '#1565c0',        // Blue - traditional
  'Newspapers': '#0d47a1',     // Dark blue - traditional
  'Radio News': '#5c6bc0',     // Indigo - traditional
  'Facebook': '#1877f2',       // Facebook blue
  'Instagram': '#e4405f',      // Instagram pink
  'Twitter/X': '#1da1f2',      // Twitter blue
  'YouTube': '#ff0000',        // YouTube red
  'Reddit': '#ff4500',         // Reddit orange
  'Podcasts': '#8e24aa',       // Purple
  'TikTok': '#000000',         // Black
}

// Identity colors - nature/culture themed
export const IDENTITY_COLORS: Record<string, string> = {
  'Canadian': '#d71920',                       // Red (flag)
  'Nature-lover': '#2e7d32',                   // Green
  'Traditional': '#8d6e63',                    // Brown
  'Wellness lifestyle': '#7cb342',             // Light green
  'Settler': '#795548',                        // Brown
  'New Canadian or Immigrant': '#ff9800',      // Orange
  'Indigenous or First Nations': '#e65100',    // Deep orange
  'Angler': '#0277bd',                         // Blue (water)
  'Hunter': '#5d4037',                         // Dark brown
  'Temporary foreign worker or student': '#9c27b0', // Purple
}

// Region (Detailed) zone mapping - based on majority rural/urban composition
// Rural = >= 50% rural respondents in that region
export const REGION_DETAILED_ZONES: Record<string, 'Urban' | 'Rural'> = {
  // British Columbia - all urban
  'British Columbia – Lower Mainland & Southwest': 'Urban',       // 2.8% rural
  'British Columbia – Vancouver Island & Coast': 'Urban',         // 12.4% rural
  'British Columbia – Thompson–Okanagan & Kootenay': 'Urban',     // 36.2% rural
  'British Columbia – Cariboo & North': 'Urban',                  // 31.8% rural

  // Alberta - all urban
  'Alberta – Calgary': 'Urban',                                   // 1.9% rural
  'Alberta – Edmonton': 'Urban',                                  // 1.8% rural
  'Alberta – Southern & Central': 'Urban',                        // 28.7% rural
  'Alberta – Northern & Mountain': 'Urban',                       // 42.9% rural

  // Saskatchewan - 2 urban, 1 rural
  'Saskatchewan – Regina & Moose Mountain': 'Urban',              // 14.6% rural
  'Saskatchewan – Saskatoon & Biggar': 'Urban',                   // 21.4% rural
  'Saskatchewan – Prince Albert & Surrounding': 'Rural',          // 59.7% rural

  // Manitoba - 1 urban, 2 rural
  'Manitoba – Winnipeg': 'Urban',                                 // 0% rural
  'Manitoba – Southern (Southeast, South Central & Southwest)': 'Rural', // 56.2% rural
  'Manitoba – Interlake & North': 'Rural',                        // 58.3% rural

  // Ontario - all urban
  'Ontario – Toronto': 'Urban',                                   // 1.3% rural
  'Ontario – Hamilton–Niagara Peninsula': 'Urban',                // 10.9% rural
  'Ontario – Kitchener–Waterloo & London': 'Urban',               // 9.7% rural
  'Ontario – Ottawa & Kingston–Pembroke': 'Urban',                // 20.7% rural
  'Ontario – Muskoka–Kawarthas, Barrie & Stratford–Bruce Peninsula': 'Urban', // 23.0% rural
  'Ontario – Windsor & Sarnia': 'Urban',                          // 20.6% rural
  'Ontario – Northwest & Northeast': 'Urban',                     // 29.8% rural

  // Quebec - all urban
  'Quebec – Montréal & Laval': 'Urban',                           // 0% rural
  'Quebec – Capitale-Nationale': 'Urban',                         // 11.6% rural
  'Quebec – Montérégie, Centre-du-Québec & Estrie': 'Urban',      // 18.0% rural
  'Quebec – Lanaudière & Laurentides': 'Urban',                   // 22.3% rural
  'Quebec – Outaouais': 'Urban',                                  // 24.6% rural
  'Quebec – Saguenay & Eastern Regions': 'Urban',                 // 34.8% rural
  'Quebec – Abitibi-Témiscamingue, Mauricie & Nord-du-Québec': 'Urban', // 27.9% rural

  // New Brunswick - all urban
  'New Brunswick – Saint John–St. Stephen': 'Urban',              // 0% rural
  'New Brunswick – Moncton–Richibucto': 'Urban',                  // 0% rural
  'New Brunswick – Fredericton & North': 'Urban',                 // 0% rural

  // Nova Scotia - 1 urban, 1 rural
  'Nova Scotia – Halifax': 'Urban',                               // 8.1% rural
  'Nova Scotia – Other Regions': 'Rural',                         // 54.9% rural

  // Prince Edward Island - mixed (46.2% rural but classified as urban)
  'Prince Edward Island': 'Urban',

  // Newfoundland & Labrador - 1 urban, 1 rural
  'Newfoundland & Labrador – Avalon Peninsula': 'Urban',          // 30.0% rural
  'Newfoundland & Labrador – Western, Central & Labrador': 'Rural', // 56.2% rural

  // Northern Territories - urban
  'Northern Territories – Yukon, Northwest Territories & Nunavut': 'Urban', // 33.1% rural
}

// Region (Detailed) colors - darker = Urban, lighter = Rural
// Based on each province's base color from PROVINCE_COLORS
export const REGION_DETAILED_COLORS: Record<string, string> = {
  // British Columbia - blue (base #4e79a7) - all urban → darker shades
  'British Columbia – Lower Mainland & Southwest': '#1a4a7a',   // Urban - darkest
  'British Columbia – Vancouver Island & Coast': '#2d5f8f',     // Urban - dark
  'British Columbia – Thompson–Okanagan & Kootenay': '#4e79a7', // Urban - medium
  'British Columbia – Cariboo & North': '#6a94bb',              // Urban - medium light

  // Alberta - orange (base #f28e2b) - all urban → darker shades
  'Alberta – Calgary': '#c45e00',              // Urban - darkest
  'Alberta – Edmonton': '#d97015',             // Urban - dark
  'Alberta – Southern & Central': '#f28e2b',   // Urban - medium
  'Alberta – Northern & Mountain': '#f9a850',  // Urban - medium light

  // Saskatchewan - red (base #e15759) - urban get darker, rural lighter
  'Saskatchewan – Regina & Moose Mountain': '#b52c2e',   // Urban - dark
  'Saskatchewan – Saskatoon & Biggar': '#d64446',        // Urban - medium dark
  'Saskatchewan – Prince Albert & Surrounding': '#f08b8d', // RURAL - light

  // Manitoba - teal (base #76b7b2) - urban dark, rural light
  'Manitoba – Winnipeg': '#3d8a84',                               // Urban - dark
  'Manitoba – Southern (Southeast, South Central & Southwest)': '#a6d9d5', // RURAL - light
  'Manitoba – Interlake & North': '#bce8e4',                      // RURAL - lightest

  // Ontario - green (base #59a14f) - all urban → darker shades
  'Ontario – Toronto': '#2d6b24',                                      // Urban - darkest
  'Ontario – Hamilton–Niagara Peninsula': '#3d8234',                   // Urban - dark
  'Ontario – Kitchener–Waterloo & London': '#4d9844',                  // Urban - medium dark
  'Ontario – Ottawa & Kingston–Pembroke': '#59a14f',                   // Urban - medium
  'Ontario – Muskoka–Kawarthas, Barrie & Stratford–Bruce Peninsula': '#6bb15f', // Urban - medium
  'Ontario – Windsor & Sarnia': '#7ec170',                             // Urban - medium light
  'Ontario – Northwest & Northeast': '#91d181',                        // Urban - light

  // Quebec - yellow/gold (base #edc949) - all urban → darker shades
  'Quebec – Montréal & Laval': '#b8940a',                              // Urban - darkest
  'Quebec – Capitale-Nationale': '#c9a51a',                            // Urban - dark
  'Quebec – Montérégie, Centre-du-Québec & Estrie': '#dab62a',         // Urban - medium dark
  'Quebec – Lanaudière & Laurentides': '#edc949',                      // Urban - medium
  'Quebec – Outaouais': '#f1d55f',                                     // Urban - medium light
  'Quebec – Saguenay & Eastern Regions': '#f5e175',                    // Urban - light
  'Quebec – Abitibi-Témiscamingue, Mauricie & Nord-du-Québec': '#f9ed8b', // Urban - lightest

  // New Brunswick - purple (base #b07aa1) - all urban → darker shades
  'New Brunswick – Saint John–St. Stephen': '#804a71',    // Urban - dark
  'New Brunswick – Moncton–Richibucto': '#9a6289',        // Urban - medium dark
  'New Brunswick – Fredericton & North': '#b07aa1',       // Urban - medium

  // Nova Scotia - pink (base #ff9da7) - urban dark, rural light
  'Nova Scotia – Halifax': '#e55d6b',          // Urban - dark
  'Nova Scotia – Other Regions': '#ffcdd2',    // RURAL - light

  // Prince Edward Island - single (urban)
  'Prince Edward Island': '#9c755f',

  // Newfoundland & Labrador - gray (base #bab0ac) - urban dark, rural light
  'Newfoundland & Labrador – Avalon Peninsula': '#8a807c',           // Urban - dark
  'Newfoundland & Labrador – Western, Central & Labrador': '#ddd6d3', // RURAL - light

  // Northern Territories - gray-teal (urban)
  'Northern Territories – Yukon, Northwest Territories & Nunavut': '#6da49b', // Urban
}

/**
 * Get zone (Urban/Rural) for a detailed region.
 */
export function getRegionDetailedZone(regionName: string): 'Urban' | 'Rural' {
  return REGION_DETAILED_ZONES[regionName] || 'Urban'
}

/**
 * Get display name for a detailed region with zone suffix.
 * Example: "Ontario – Toronto" → "Ontario – Toronto (Urban)"
 */
export function getRegionDetailedDisplayName(regionName: string): string {
  const zone = REGION_DETAILED_ZONES[regionName]
  if (zone) {
    return `${regionName} (${zone})`
  }
  return regionName
}

/**
 * Get color for a detailed region.
 */
export function getRegionDetailedColor(regionName: string): string {
  return REGION_DETAILED_COLORS[regionName] || '#bab0ac'
}

/**
 * Get color for province by name.
 */
export function getProvinceColor(provinceName: string): string {
  return PROVINCE_COLORS[provinceName] || '#bab0ac'
}

/**
 * Get color for ethnicity by name.
 */
export function getEthnicityColorByName(ethnicityName: string): string {
  return ETHNICITY_COLORS[ethnicityName] || '#bab0ac'
}

/**
 * Get color for ethnicity by index (cycles through Tableau 10).
 */
export function getEthnicityColor(index: number): string {
  return TABLEAU_10[index % TABLEAU_10.length]
}

/**
 * Get color for union membership by label.
 */
export function getUnionColor(label: string): string {
  return UNION_COLORS[label] || '#bab0ac'
}

/**
 * Get color for tribe by display name.
 */
export function getTribeColorByName(tribeName: string): string {
  const tribeNameMap: Record<string, string> = {
    'Pro-Extraction Populists': '#e15759',
    'Community-First Elders': '#f28e2b',
    'New Canadian Materialists': '#f1ce63',
    'Struggling Green Populists': '#2e7d32',
    'Indigenous Identified': '#59a14f',
    'Older Institutionalists': '#4e79a7',
    'Mainstream Progressives': '#8cd17d',
    'The Woke': '#b07aa1',
  }
  return tribeNameMap[tribeName] || '#bab0ac'
}

/**
 * Generate a color palette for N items.
 */
export function generatePalette(count: number): string[] {
  const baseColors = TABLEAU_10

  if (count <= baseColors.length) {
    return baseColors.slice(0, count)
  }

  // Generate additional colors if needed
  const palette = [...baseColors]
  while (palette.length < count) {
    const baseColor = baseColors[palette.length % baseColors.length]
    // Lighten the color slightly
    palette.push(lightenColor(baseColor, 0.3))
  }

  return palette
}

/**
 * Lighten a hex color by a percentage.
 */
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(255 * percent)
  const R = Math.min(255, (num >> 16) + amt)
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt)
  const B = Math.min(255, (num & 0x0000ff) + amt)
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`
}
